import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  BedDouble,
  Users,
  PartyPopper,
  Sparkles,
  RotateCw,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useArkemaLang } from "@/components/thales/i18n";

interface FlipCard {
  title: string;
  image: string;
  icon: React.ComponentType<{ className?: string }>;
  experience: string;
  plus: string;
}

interface SecondaryItem {
  title: string;
  image: string;
}

const ThalesEventTypes = () => {
  const { t } = useArkemaLang();
  const [api, setApi] = useState<CarouselApi>();
  const [flipped, setFlipped] = useState<number | null>(null);

  const flipCards: FlipCard[] = [
    {
      title: "Séminaires Résidentiels",
      image: "/lovable-uploads/villa-st-tropez-quiz-bg.webp",
      icon: BedDouble,
      experience: "Changez d'air pour mieux collaborer.",
      plus: "Des maisons de campagne et hôtels de charme entièrement privatisés, avec salles de réunion équipées et hébergement haut de gamme.",
    },
    {
      title: "Journées d'Étude",
      image: "/lovable-uploads/thales-awayday.webp",
      icon: Users,
      experience: "Un cadre stimulant pour vos décisions stratégiques.",
      plus: "Des espaces atypiques en plein cœur de ville ou au vert, accessibles facilement. Forfaits tout compris.",
    },
    {
      title: "Soirées d'Entreprise",
      image: "/lovable-uploads/thales-parties.webp",
      icon: PartyPopper,
      experience: "Célébrez vos succès dans des lieux spectaculaires.",
      plus: "Rooftops avec vue, lofts industriels ou jardins secrets. Nous gérons le traiteur, le staff et l'ambiance.",
    },
    {
      title: "Activité team building",
      image: "/lovable-uploads/thales-teambuilding.webp",
      icon: Sparkles,
      experience: "Renforcez les liens par l'action et le partage.",
      plus: "Des activités originales (ateliers cuisine, rallyes urbains, challenges sportifs) intégrées directement sur votre lieu.",
    },
  ];

  const secondaryItems: SecondaryItem[] = [
    { title: "Cocktails déjeunatoires", image: "/lovable-uploads/thales-meals.webp" },
    { title: "Lancements de produits", image: "/lovable-uploads/thales-rooms.webp" },
    { title: "Conférences", image: "/lovable-uploads/thales-conferences.webp" },
    { title: "Afterworks", image: "/lovable-uploads/thales-parties.webp" },
  ];

  useEffect(() => {
    if (!api) return;
  }, [api]);

  const toggleFlip = (i: number) => {
    setFlipped((prev) => (prev === i ? null : i));
  };

  return (
    <section className="pt-4 md:pt-8 pb-6 md:pb-8 px-4 bg-white">
      <div className="max-w-[1280px] mx-auto">
        <div className="mb-8 md:mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-normal text-[#111111] max-w-3xl mx-auto leading-tight">
            {t("events.title")}
          </h2>
        </div>

        {/* Niveau 1 — Grille 4 Flip Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {flipCards.map((card, index) => {
            const Icon = card.icon;
            const isFlipped = flipped === index;
            return (
              <div
                key={index}
                className="group [perspective:1200px] aspect-[4/5]"
                onClick={() => toggleFlip(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleFlip(index);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={isFlipped}
                aria-label={`${card.title} — voir le détail`}
              >
                <div
                  className={`relative w-full h-full [transform-style:preserve-3d] transition-transform duration-700 ease-out group-hover:[transform:rotateY(180deg)] ${
                    isFlipped ? "[transform:rotateY(180deg)]" : ""
                  }`}
                >
                  {/* Recto */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-sm [backface-visibility:hidden]">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                    <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-white/80 text-[11px] font-medium bg-white/10 backdrop-blur-sm rounded-full px-2.5 py-1">
                      <RotateCw className="w-3 h-3" />
                      <span>En savoir +</span>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                      <h3 className="text-white text-2xl md:text-3xl font-medium leading-tight">
                        {card.title}
                      </h3>
                    </div>
                  </div>

                  {/* Verso */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden bg-white border border-black/10 shadow-sm [backface-visibility:hidden] [transform:rotateY(180deg)] p-5 md:p-6 flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-full bg-[#C6E278] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-[#111111]" />
                      </div>
                      <h3 className="text-[#111111] text-lg md:text-xl font-medium leading-tight">
                        {card.title}
                      </h3>
                    </div>

                    <div className="space-y-4 flex-1 overflow-auto">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#C6E278] mb-1.5">
                          L'expérience
                        </div>
                        <p className="text-sm md:text-base text-[#111111] leading-snug">
                          {card.experience}
                        </p>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#C6E278] mb-1.5">
                          Le plus Naboo
                        </div>
                        <p className="text-sm text-[#4b5563] leading-relaxed">
                          {card.plus}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Niveau 2 — Carrousel "Bien plus encore" */}
        <div className="mt-12 md:mt-14">
          <div className="text-xs sm:text-sm font-medium uppercase tracking-wider text-[#6b7280] mb-4 text-center">
            Bien plus encore
          </div>

          <div className="relative">
            <Carousel
              setApi={setApi}
              opts={{ align: "start", loop: true }}
              plugins={[Autoplay({ delay: 4500, stopOnInteraction: true })]}
              className="w-full"
            >
              <CarouselContent className="-ml-3 justify-center">
                {secondaryItems.map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="pl-3 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5"
                  >
                    <div className="group relative rounded-xl overflow-hidden aspect-square cursor-pointer">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <span className="text-white text-lg md:text-xl font-medium leading-tight">
                          {item.title}
                        </span>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              aria-label={t("events.prev")}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-9 h-9 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] shadow-sm transition-colors items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4 text-[#111111]" />
            </button>
            <button
              type="button"
              onClick={() => api?.scrollNext()}
              aria-label={t("events.next")}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-9 h-9 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] shadow-sm transition-colors items-center justify-center"
            >
              <ChevronRight className="w-4 h-4 text-[#111111]" />
            </button>

            <div className="flex md:hidden items-center justify-center gap-3 mt-4">
              <button
                type="button"
                onClick={() => api?.scrollPrev()}
                aria-label={t("events.prev")}
                className="w-9 h-9 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] transition-colors flex items-center justify-center"
              >
                <ChevronLeft className="w-4 h-4 text-[#111111]" />
              </button>
              <button
                type="button"
                onClick={() => api?.scrollNext()}
                aria-label={t("events.next")}
                className="w-9 h-9 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] transition-colors flex items-center justify-center"
              >
                <ChevronRight className="w-4 h-4 text-[#111111]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThalesEventTypes;
